/**
 * Interface for CircularSpinner component props
 * Following TypeScript conventions with readonly properties
 */
interface CircularSpinnerProps {
  readonly size?: "small" | "medium" | "large" | "xl";
  readonly color?:
    | "primary"
    | "secondary"
    | "success"
    | "warning"
    | "danger"
    | "white";
  readonly thickness?: "thin" | "medium" | "thick";
  readonly text?: string;
  readonly className?: string;
  readonly showText?: boolean;
  readonly inline?: boolean;
}

/**
 * Circular Spinner Component
 * A reusable loading spinner following Clean Architecture principles
 *
 * @example
 * // Basic usage
 * <CircularSpinner />
 *
 * @example
 * // With custom props
 * <CircularSpinner size="large" color="primary" text="Loading data..." />
 *
 * @example
 * // Inline spinner
 * <CircularSpinner size="small" inline />
 */
const CircularSpinner = ({
  size = "medium",
  color = "primary",
  thickness = "medium",
  text,
  className = "",
  showText = true,
  inline = false,
}: CircularSpinnerProps) => {
  // Size mappings following consistent design system
  const sizeClasses = {
    small: "w-4 h-4",
    medium: "w-8 h-8",
    large: "w-12 h-12",
    xl: "w-16 h-16",
  } as const;

  // Color mappings using CSS variables for consistency
  const colorClasses = {
    primary: "border-[color:var(--color-cbre-green)]",
    secondary: "border-gray-600",
    success: "border-green-600",
    warning: "border-yellow-600",
    danger: "border-red-600",
    white: "border-white",
  } as const;

  // Thickness mappings for border width
  const thicknessClasses = {
    thin: "border-2",
    medium: "border-4",
    thick: "border-8",
  } as const;

  // Text size mappings based on spinner size
  const textSizeClasses = {
    small: "text-xs",
    medium: "text-sm",
    large: "text-base",
    xl: "text-lg",
  } as const;

  // Spinner element with proper accessibility
  const spinnerElement = (
    <div
      className={`
        ${sizeClasses[size]}
        ${thicknessClasses[thickness]}
        ${colorClasses[color]}
        border-solid
        border-t-transparent
        rounded-full
        animate-spin
        ${className}
      `
        .replace(/\s+/g, " ")
        .trim()}
      role="status"
      aria-label={text || "Loading"}
    >
      <span className="sr-only">{text || "Loading..."}</span>
    </div>
  );

  // Inline version - just the spinner
  if (inline) {
    return spinnerElement;
  }

  // Full version with optional text
  return (
    <div className="flex flex-col items-center justify-center p-4">
      {spinnerElement}
      {showText && text && (
        <p
          className={`mt-3 text-gray-600 ${textSizeClasses[size]} text-center`}
        >
          {text}
        </p>
      )}
    </div>
  );
};

export default CircularSpinner;
